import * as THREE from "three";
import { Line, Polygon, Vector3 } from "opengeometry";
import { Glyphs } from "@opengeometry/openglyph";

import { ElementType } from "../elements/base-type";
import { OPElement, IPlaceable } from "../elements/op-element";
import { IShape } from "../shapes/base-type";
import { Event } from "../../../../utils/event";
import type { Vec3 } from "../types";
import {
  DatumOptionsBase,
  DatumType,
  DATUM_COLORS,
} from "./base-type";
import { applyVisibility, setProfileView, setModelView } from "./internals/visibility";
import { disposeSubElements } from "./internals/lifecycle";

const LABEL_FONT_SIZE = 0.3;

export interface SurveyPoint {
  latitude: number;
  longitude: number;
  elevation: number;
  description?: string;
}

export interface ProjectOriginOptions extends DatumOptionsBase {
  datumType: DatumType.PROJECT_ORIGIN;
  /** World-space location of the project zero. */
  position: [number, number, number];
  /** Rotation about world Y (radians) applied to the site axes. */
  rotationY: number;
  /** Arm length of the tri-axis gizmo. */
  gizmoSize: number;
  /** Optional survey fix that this origin maps to. */
  surveyPoint?: SurveyPoint;
  /** Whether the triangular benchmark symbol is shown. */
  showBenchmark: boolean;
}

/**
 * Project origin / benchmark datum. Only one instance is normally valid
 * per project; callers are expected to enforce that themselves (e.g. via
 * `getElementsByType(ProjectOrigin)`).
 */
export class ProjectOrigin extends OPElement implements IShape, IPlaceable {
  ogid: string = crypto.randomUUID();
  ogType: string = ElementType.DATUM_PROJECT_ORIGIN;

  subElements2D: Map<string, THREE.Object3D> = new Map();
  subElements3D: Map<string, THREE.Object3D> = new Map();

  selected = false;
  edit = false;
  locked = false;

  onDatumUpdated: Event<null> = new Event();

  private _profileView = true;
  private _modelView = true;

  propertySet: ProjectOriginOptions = {
    labelName: "Project Origin",
    datumType: DatumType.PROJECT_ORIGIN,
    color: DATUM_COLORS.ORIGIN,
    lineWidth: 2,
    visibility: { plan: true, model: true },
    position: [0, 0, 0],
    rotationY: 0,
    gizmoSize: 1,
    surveyPoint: undefined,
    showBenchmark: false,
  };

  constructor(config?: Partial<ProjectOriginOptions>) {
    super();
    if (config) {
      this.propertySet = {
        ...this.propertySet,
        ...config,
        visibility: {
          ...this.propertySet.visibility,
          ...(config.visibility ?? {}),
        },
      };
    }
    this.propertySet.ogid = this.ogid;
    this.setOPGeometry();
    const resolved = applyVisibility(
      this.subElements2D,
      this.subElements3D,
      this.propertySet.visibility,
    );
    this._profileView = resolved.plan;
    this._modelView = resolved.model;
  }

  move(delta: Vec3): void {
    const [px, py, pz] = this.propertySet.position;
    this.propertySet.position = [px + delta[0], py + delta[1], pz + delta[2]];
    this.setOPGeometry();
    this.onDatumUpdated.trigger(null);
  }

  rotate(angleDeg: number, _axis?: Vec3, _anchor?: Vec3): void {
    this.propertySet.rotationY += (angleDeg * Math.PI) / 180;
    this.setOPGeometry();
    this.onDatumUpdated.trigger(null);
  }

  get labelName(): string {
    return this.propertySet.labelName;
  }
  set labelName(value: string) {
    this.propertySet.labelName = value;
    this.setOPGeometry();
  }

  get datumType(): DatumType {
    return this.propertySet.datumType;
  }

  get profileView(): boolean {
    return this._profileView;
  }
  set profileView(value: boolean) {
    this._profileView = value;
    setProfileView(this.subElements2D, value);
  }

  get modelView(): boolean {
    return this._modelView;
  }
  set modelView(value: boolean) {
    this._modelView = value;
    setModelView(this.subElements3D, value);
  }

  setOPConfig(config: Partial<ProjectOriginOptions>): void {
    disposeSubElements(this.subElements2D, this.subElements3D);
    this.propertySet = {
      ...this.propertySet,
      ...config,
      visibility: {
        ...this.propertySet.visibility,
        ...(config.visibility ?? {}),
      },
    };
    this.setOPGeometry();
    this.setOPMaterial();
  }

  getOPConfig(): ProjectOriginOptions {
    return this.propertySet;
  }

  setOPMaterial(): void {
    // Intentionally empty — geometry rebuilds handle every visual update.
  }

  setOPGeometry(): void {
    disposeSubElements(this.subElements2D, this.subElements3D);
    const { position, rotationY, gizmoSize, lineWidth, showBenchmark } = this.propertySet;
    const [ox, oy, oz] = position;
    const w = lineWidth ?? 2;

    const cos = Math.cos(rotationY);
    const sin = Math.sin(rotationY);
    const rot = (dx: number, dz: number) =>
      new Vector3(ox + cos * dx - sin * dz, oy, oz + sin * dx + cos * dz);

    const origin = new Vector3(ox, oy, oz);

    const xEnd = rot(gizmoSize, 0);
    const zEnd = rot(0, gizmoSize);
    const yEnd = new Vector3(ox, oy + gizmoSize, oz);

    const xAxis = new Line({ start: origin, end: xEnd, color: 0xe74c3c, fatLines: true, width: w });
    const yAxis = new Line({ start: origin, end: yEnd, color: 0x2ecc71, fatLines: true, width: w });
    const zAxis = new Line({ start: origin, end: zEnd, color: 0x3498db, fatLines: true, width: w });

    this.add(xAxis);
    this.add(yAxis);
    this.add(zAxis);
    this.subElements3D.set("xAxis", xAxis);
    this.subElements3D.set("yAxis", yAxis);
    this.subElements3D.set("zAxis", zAxis);

    const tickSize = gizmoSize * 0.12;
    const xTickA = rot(gizmoSize, -tickSize);
    const xTickB = rot(gizmoSize, tickSize);
    const zTickA = rot(-tickSize, gizmoSize);
    const zTickB = rot(tickSize, gizmoSize);
    this.subElements3D.set(
      "xTick",
      this.addLine(new Line({ start: xTickA, end: xTickB, color: 0xe74c3c, fatLines: true, width: w })),
    );
    this.subElements3D.set(
      "zTick",
      this.addLine(new Line({ start: zTickA, end: zTickB, color: 0x3498db, fatLines: true, width: w })),
    );

    if (showBenchmark) {
      const size = gizmoSize * 0.35;
      const apex = new Vector3(ox, oy + size, oz);
      const baseLeft = new Vector3(ox - size * 0.7, oy - size * 0.2, oz);
      const baseRight = new Vector3(ox + size * 0.7, oy - size * 0.2, oz);
      const bench = new Polygon({
        vertices: [apex, baseLeft, baseRight],
        color: DATUM_COLORS.ORIGIN,
      });
      bench.outline = true;
      bench.outlineColor = DATUM_COLORS.ORIGIN;
      this.add(bench);
      this.subElements3D.set("benchmark", bench);
    }

    // Label positioned slightly offset from origin so it doesn't overlap the gizmo.
    const label = Glyphs.addGlyph(this.propertySet.labelName, LABEL_FONT_SIZE, DATUM_COLORS.ORIGIN, true);
    label.textMesh.geometry.computeBoundingBox();
    const bbox = label.textMesh.geometry.boundingBox!;
    const halfWidth = (bbox.max.x - bbox.min.x) * 0.5;
    const halfDepth = (bbox.max.y - bbox.min.y) * 0.5;
    label.position.set(
      ox - halfWidth + gizmoSize * 0.15,
      oy,
      oz - halfDepth + gizmoSize * 0.15,
    );
    this.add(label);
    this.subElements3D.set("label", label);

    this.onDatumUpdated.trigger(null);
  }

  private addLine(line: Line): Line {
    this.add(line);
    return line;
  }
}
